import type { PaletteOptions } from '@mui/material/styles'

import type { RoleColors } from './roles'

/**
 * Bridges the role layer onto the slots MUI's own components read.
 *
 * It is short on purpose, and the test a slot has to pass to belong here is: is it opaque?
 * `text.secondary`, `text.disabled`, `divider` and the colours under `action` are all defined by MUI as an
 * alpha over whatever surface sits beneath them, black in light and white in dark, so they compose over
 * the component's own background and follow the mode without being told. Replacing them with opaque brand
 * values would trade something already in the right shape for something decided twice and then kept in
 * step.
 *
 * The slots below fail that test: MUI picks an opaque value for each, and an opaque value it picked has
 * no relation to this palette and no reason to follow the mode.
 *
 * `background.default` and `text.primary` matter more here than they do in the app frontend, because this
 * application mounts `CssBaseline` - those two slots are literally what it writes onto `body`. They are
 * also the two properties the pre-paint sheet in `index.html` commits to, one for one, so they have to
 * keep agreeing with it; `verify:color-mode` asserts that.
 *
 * `background.paper` left to MUI resolves to `#121212` in dark, which measures 1.02 against this
 * application's canvas - so a raised surface does not clash, it goes flat. The elevation disappears, and a
 * reviewer looking for a wrong colour finds nothing.
 *
 * `error` is here because MUI's form components read `error.main` directly - the ink of a
 * `FormHelperText.Mui-error`, the border of an errored field - and its default is `#d32f2f` in light and
 * `#f44336` in dark. A field validated by a plain `TextField` would show a red no gate measures, next to a
 * field wrapped by `ui-input` showing another one: two reds for one state, in one form.
 *
 * `primary` is the slot MUI reaches for without being asked: the default `color` of `CircularProgress`,
 * `Radio`, `Checkbox` and `Slider`, and the focus ring of a `TextField`. `ui-styles` sets `primary.main`
 * to the page colour, so all of them paint at about 1.04 on a light surface - a spinner that turns
 * invisibly reads as a page that never loads.
 *
 * IMPORTANT: `warning`, `success` and `info` are deliberately not bridged - nothing in this tree consumes
 * them, by prop or by direct read. The moment a `severity` or a `color='success'` appears, it belongs here
 * on the same argument as `error`.
 *
 * IMPORTANT: `primary` is also the one bridged family that carries design-system tokens, so `index.ts`
 * merges it instead of spreading it over them. What this returns for it is a delta, not the family.
 */
export const muiSlots = (colors: RoleColors): PaletteOptions => ({
  background: {
    default: colors.surface,
    paper: colors.surfaceRaised,
  },
  text: {
    primary: colors.onSurface,
  },
  error: {
    main: colors.error,
    contrastText: colors.onError,
  },
  primary: {
    main: colors.primary,
    contrastText: colors.onPrimary,
  },
})
